import { IEditorV3 } from "@azu-business/editor-v3";
import { AioExternalReplacements, AioExternalSingle, AitRowGroupData, ARS } from "aic";
import { CommentPanelStatus } from "components/common/comments/interface";
import { CommentReadDto } from "functions/api/comments/interface";
import { eAccessRights } from "functions/api/enums";
import { DomainReplacements } from "functions/api/hierarchy/DomainReplacements";
import { HierarchyReadDto } from "functions/api/hierarchy/HierarchyReadDto";
import { HierarchySaveDto } from "functions/api/hierarchy/saveHierarchyData";
import { SaveTocAsMajorVersionParams } from "functions/api/hierarchy/saveTocAsMajorVersion";
import { IRequestStatus } from "functions/api/interface";
import {
  ClinicalCodeListReadDto,
  ClinicalCommentReadDto,
  ClinicalDatasetReadDto,
  ClinicalLibraryReadDto,
  ClinicalMethodReadDto,
  ClinicalValueListReadDto,
  ClinicalWhereClauseReadDto,
} from "functions/api/library/interface";
import { TlfItemIndexReadDto } from "functions/api/tlfItem/TlfItemIndexReadDto";
import { Patch } from "rfc6902";

export enum eSingleSource {
  hierarchy = "Hierarchy",
  tlfItem = "TlfItem",
  domain = "Domain",
}

/**
 * Extend external single replacement with source
 */
export interface AioExternalSingleWithSource<T> extends AioExternalSingle<T> {
  /** Source of the replacement for released templates */
  source: eSingleSource;
}

/**
 * Extended external single replacement with hierarchy provenance tracking.
 * Includes the ID of the hierarchy from which this replacement originated.
 */
export interface AioExternalSingleWithHierarchy<T> extends AioExternalSingleWithSource<T> {
  /**
   * ID of the hierarchy this replacement originated from
   */
  fromHierarchyId: string;
}

/**
 * Extended external replacements list with hierarchy provenance tracking.
 * Includes the ID of the hierarchy from which this replacement list originated.
 */
export interface AioExternalReplacementsWithHierarchy<T> extends AioExternalReplacements<T> {
  /**
   * ID of the hierarchy this replacement list originated from
   */
  fromHierarchyId: string;
}

/**
 * Extended row group with hierarchy provenance tracking.
 * Includes the ID of the hierarchy from which this row group originated.
 */
export interface AitRowGroupDataWithHierarchy<T> extends AitRowGroupData<T> {
  /**
   * ID of the hierarchy this row group originated from
   */
  fromHierarchyId: string;
}

/**
 * Extended analysis set with hierarchy provenance tracking.
 * Includes the ID of the hierarchy from which this analysis set originated.
 */
export type AnalysisSetWithHierarchy = ARS.AnalysisSet & {
  /**
   * ID of the hierarchy this analysis set originated from
   */
  fromHierarchyId: string;
};

/**
 * Extended data subset with hierarchy provenance tracking.
 * Includes the ID of the hierarchy from which this data subset originated.
 */
export type DataSubsetWithHierarchy = ARS.DataSubset & {
  /**
   * ID of the hierarchy this data subset originated from
   */
  fromHierarchyId: string;
};

/**
 * Extended grouping factor with hierarchy provenance tracking.
 * Includes the ID of the hierarchy from which this grouping factor originated.
 */
export type GroupingFactorWithHierarchy = ARS.GroupingFactor & {
  /**
   * ID of the hierarchy this grouping factor originated from
   */
  fromHierarchyId: string;
};

/**
 * Parent templates aggregated from the parent hierarchies
 */
export interface HierarchyParentTemplates {
  clinicalDatasets: ClinicalDatasetReadDto[];
  clinicalCodeLists: ClinicalCodeListReadDto[];
  clinicalValueLists: ClinicalValueListReadDto[];
  clinicalComments: ClinicalCommentReadDto[];
  clinicalMethods: ClinicalMethodReadDto[];
  clinicalWhereClauses: ClinicalWhereClauseReadDto[];
  domainReplacements: DomainReplacements[];
  lists: AioExternalReplacementsWithHierarchy<IEditorV3>[];
  rowGroups: AitRowGroupDataWithHierarchy<IEditorV3>[];
  singles: AioExternalSingleWithHierarchy<IEditorV3>[];
  analysisSets: AnalysisSetWithHierarchy[];
  dataSubsets: DataSubsetWithHierarchy[];
  groupingFactors: GroupingFactorWithHierarchy[];
}

/**
 * Save blockers for the hierarchy context, tracking items that prevent saving
 */
export interface ISaveBlockers {
  /** Set of single replacements that are blocking save, each with an airid and warning text */
  singles: Set<{ airid: string; warningText: string }>;
  /** Set of domain replacements that are blocking save, each with an airid and warning text */
  domainReplacements: Set<{ airid: string; warningText: string }>;
  /** Optional hierarchy name validation error message blocking save */
  hierarchyName?: string;
}

// Hierarchy context
export interface HierarchyContextProps {
  id: string;
  accessRights: eAccessRights[];
  changed: boolean;
  commentPanelStatus: CommentPanelStatus;
  doGetAccessRights: boolean;
  doSave: boolean;
  doTocSave: boolean;
  hierarchy: HierarchyReadDto | null;
  hierarchyNodes: HierarchyReadDto[];
  isClinicalLibraryLoading: boolean;
  isSubscribed: boolean;
  parentTemplates: HierarchyParentTemplates;
  prefix: string;
  saveBlockers: ISaveBlockers;
  saveTocDescription?: string;
  savedHierarchy: HierarchyReadDto | null;
  tlfItemIndex: TlfItemIndexReadDto[] | null;
  isNameChanged: boolean;
}

export const ADD_DOMAIN_REPLACEMENT = "ADD_DOMAIN_REPLACEMENT";
export const ADD_HIERARCHY_NODES = "ADD_HIERARCHY_NODES";
export const ADD_SINGLE_REPLACEMENT = "ADD_SINGLE_REPLACEMENT";
export const LOAD_HIERARCHY = "LOAD_HIERARCHY";
export const PATCH_HIERARCHY_COMMENTS = "PATCH_HIERARCHY_COMMENTS";
export const LOAD_CLINICAL_LIBRARY = "LOAD_CLINICAL_LIBRARY";
export const REMOVE_DOMAIN_REPLACEMENT = "REMOVE_DOMAIN_REPLACEMENT";
export const REMOVE_HIERARCHY_COMMENT = "REMOVE_HIERARCHY_COMMENT";
export const REMOVE_HIERARCHY_NODE = "REMOVE_HIERARCHY_NODE";
export const REMOVE_SINGLE_REPLACEMENT = "REMOVE_SINGLE_REPLACEMENT";
export const SAVE_HIERARCHY = "SAVE_HIERARCHY";
export const SAVE_HIERARCHY_COMPLETED = "SAVE_HIERARCHY_COMPLETED";
export const SAVE_HIERARCHY_FAILED = "SAVE_HIERARCHY_FAILED";
export const SAVE_TOC_MAJOR_VERSION = "SAVE_TOC_MAJOR_VERSION";
export const SET_ACCESS_RIGHTS = "SET_ACCESS_RIGHTS";
export const SET_CLINICAL_LIBRARY_LOADING = "SET_CLINICAL_LIBRARY_LOADING";
export const SET_HIERARCHY_COMMENT_PANEL = "SET_HIERARCHY_COMMENT_PANEL";
export const SET_HIERARCHY_ID = "SET_HIERARCHY_ID";
export const SET_HIERARCHY_NAME_SAVE_BLOCKER = "SET_HIERARCHY_NAME_SAVE_BLOCKER";
export const SET_HIERARCHY_SUBSCRIPTION_STATUS = "SET_HIERARCHY_SUBSCRIPTION_STATUS";
export const UPDATE_DOMAIN_REPLACEMENT = "UPDATE_DOMAIN_REPLACEMENT";
export const UPDATE_HIERARCHY = "UPDATE_HIERARCHY";
export const UPDATE_HIERARCHY_COMMENT = "UPDATE_HIERARCHY_COMMENT";
export const UPDATE_SINGLE_REPLACEMENT = "UPDATE_SINGLE_REPLACEMENT";
export const UPDATE_TLF_ITEM_INDEX = "UPDATE_TLF_ITEM_INDEX";

export type HierarchyOperation =
  | typeof ADD_DOMAIN_REPLACEMENT
  | typeof ADD_HIERARCHY_NODES
  | typeof ADD_SINGLE_REPLACEMENT
  | typeof LOAD_HIERARCHY
  | typeof LOAD_CLINICAL_LIBRARY
  | typeof PATCH_HIERARCHY_COMMENTS
  | typeof REMOVE_DOMAIN_REPLACEMENT
  | typeof REMOVE_HIERARCHY_COMMENT
  | typeof REMOVE_HIERARCHY_NODE
  | typeof REMOVE_SINGLE_REPLACEMENT
  | typeof SAVE_HIERARCHY
  | typeof SAVE_HIERARCHY_COMPLETED
  | typeof SAVE_HIERARCHY_FAILED
  | typeof SAVE_TOC_MAJOR_VERSION
  | typeof SET_ACCESS_RIGHTS
  | typeof SET_CLINICAL_LIBRARY_LOADING
  | typeof SET_HIERARCHY_COMMENT_PANEL
  | typeof SET_HIERARCHY_ID
  | typeof SET_HIERARCHY_NAME_SAVE_BLOCKER
  | typeof SET_HIERARCHY_SUBSCRIPTION_STATUS
  | typeof UPDATE_DOMAIN_REPLACEMENT
  | typeof UPDATE_HIERARCHY
  | typeof UPDATE_HIERARCHY_COMMENT
  | typeof UPDATE_SINGLE_REPLACEMENT
  | typeof UPDATE_TLF_ITEM_INDEX;

export interface IHierarchyActionBase {
  operation: HierarchyOperation;
}

export interface IHierarchyActionAddDomainReplacement extends IHierarchyActionBase {
  operation: typeof ADD_DOMAIN_REPLACEMENT;
  domainReplacement: DomainReplacements;
}

export interface IHierarchyActionAddHierarchyNodes extends IHierarchyActionBase {
  operation: typeof ADD_HIERARCHY_NODES;
  hierarchyNodes: HierarchyReadDto[];
}

export interface IHierarchyActionAddSingleReplacement extends IHierarchyActionBase {
  operation: typeof ADD_SINGLE_REPLACEMENT;
  single: AioExternalSingleWithSource<IEditorV3>;
}

export interface IHierarchyActionLoadClinicalLibrary extends IHierarchyActionBase {
  operation: typeof LOAD_CLINICAL_LIBRARY;
  clinicalLibrary: ClinicalLibraryReadDto[];
}

export interface IHierarchyActionLoadHierarchy extends IHierarchyActionBase {
  operation: typeof LOAD_HIERARCHY;
  accessRights: eAccessRights[];
  hierarchy: HierarchyReadDto;
  hierarchyNodes: HierarchyReadDto[];
  isSubscribed: boolean;
}

export interface IHierarchyActionPatchHierarchyComments extends IHierarchyActionBase {
  operation: typeof PATCH_HIERARCHY_COMMENTS;
  patch: Patch;
}

export interface IHierarchyActionRemoveDomainReplacement extends IHierarchyActionBase {
  operation: typeof REMOVE_DOMAIN_REPLACEMENT;
  domainReplacement: DomainReplacements;
}

export interface IHierarchyActionRemoveHierarchyComment extends IHierarchyActionBase {
  operation: typeof REMOVE_HIERARCHY_COMMENT;
  commentId: string;
}

export interface IHierarchyActionRemoveHierarchyNode extends IHierarchyActionBase {
  operation: typeof REMOVE_HIERARCHY_NODE;
  hierarchyId: string;
}

export interface IHierarchyActionRemoveSingleReplacement extends IHierarchyActionBase {
  operation: typeof REMOVE_SINGLE_REPLACEMENT;
  single: AioExternalSingleWithSource<IEditorV3>;
}

export interface IHierarchyActionSaveHierarchy extends IHierarchyActionBase {
  operation: typeof SAVE_HIERARCHY;
}

export interface IHierarchyActionSaveHierarchyCompleted extends IHierarchyActionBase {
  operation: typeof SAVE_HIERARCHY_COMPLETED;
  hierarchy: HierarchyReadDto;
  oldHierarchyId?: string;
}

export interface IHierarchyActionSaveHierarchyFailed extends IHierarchyActionBase {
  operation: typeof SAVE_HIERARCHY_FAILED;
}

export interface IHierarchyActionSaveTocMajorVersion extends IHierarchyActionBase {
  operation: typeof SAVE_TOC_MAJOR_VERSION;
  saveTocDescription: string;
  oldHierarchyId?: string;
}

export interface IHierarchyActionSetAccessRights extends IHierarchyActionBase {
  operation: typeof SET_ACCESS_RIGHTS;
  accessRights: eAccessRights[];
}

export interface IHierarchyActionSetClinicalLibraryLoading extends IHierarchyActionBase {
  operation: typeof SET_CLINICAL_LIBRARY_LOADING;
  isClinicalLibraryLoading: boolean;
}

export interface IHierarchyActionSetHierarchyCommentPanel extends IHierarchyActionBase {
  operation: typeof SET_HIERARCHY_COMMENT_PANEL;
  commentPanelStatus: Partial<CommentPanelStatus>;
}

export interface IHierarchyActionSetHierarchyId extends IHierarchyActionBase {
  operation: typeof SET_HIERARCHY_ID;
  hierarchyId: string;
}

export interface IHierarchyActionSetHierarchyNameSaveBlocker extends IHierarchyActionBase {
  operation: typeof SET_HIERARCHY_NAME_SAVE_BLOCKER;
  warningText?: string;
}

export interface IHierarchyActionSetSubscriptionStatus extends IHierarchyActionBase {
  operation: typeof SET_HIERARCHY_SUBSCRIPTION_STATUS;
  isSubscribed: boolean;
}

export interface IHierarchyActionUpdateDomainReplacement extends IHierarchyActionBase {
  operation: typeof UPDATE_DOMAIN_REPLACEMENT;
  domainReplacement: DomainReplacements;
}

export interface IHierarchyActionUpdateHierarchy extends IHierarchyActionBase {
  operation: typeof UPDATE_HIERARCHY;
  hierarchy: Partial<HierarchyReadDto>;
  savedHierarchy?: HierarchyReadDto;
  isNameChanged?: boolean;
}

export interface IHierarchyActionUpdateHierarchyComment extends IHierarchyActionBase {
  operation: typeof UPDATE_HIERARCHY_COMMENT;
  comment: CommentReadDto;
}

export interface IHierarchyActionUpdateSingleReplacement extends IHierarchyActionBase {
  operation: typeof UPDATE_SINGLE_REPLACEMENT;
  single: AioExternalSingleWithSource<IEditorV3>;
}

export interface IHierarchyActionUpdateTlfItemIndex extends IHierarchyActionBase {
  operation: typeof UPDATE_TLF_ITEM_INDEX;
  tlfItemIndex: TlfItemIndexReadDto[] | null;
  tocMinorVersion?: number;
}

export type IHierarchyAction =
  | IHierarchyActionAddDomainReplacement
  | IHierarchyActionAddHierarchyNodes
  | IHierarchyActionAddSingleReplacement
  | IHierarchyActionLoadClinicalLibrary
  | IHierarchyActionLoadHierarchy
  | IHierarchyActionPatchHierarchyComments
  | IHierarchyActionRemoveDomainReplacement
  | IHierarchyActionRemoveHierarchyComment
  | IHierarchyActionRemoveHierarchyNode
  | IHierarchyActionRemoveSingleReplacement
  | IHierarchyActionSaveHierarchy
  | IHierarchyActionSaveHierarchyCompleted
  | IHierarchyActionSaveHierarchyFailed
  | IHierarchyActionSaveTocMajorVersion
  | IHierarchyActionSetAccessRights
  | IHierarchyActionSetClinicalLibraryLoading
  | IHierarchyActionSetHierarchyCommentPanel
  | IHierarchyActionSetHierarchyId
  | IHierarchyActionSetHierarchyNameSaveBlocker
  | IHierarchyActionSetSubscriptionStatus
  | IHierarchyActionUpdateDomainReplacement
  | IHierarchyActionUpdateHierarchy
  | IHierarchyActionUpdateHierarchyComment
  | IHierarchyActionUpdateSingleReplacement
  | IHierarchyActionUpdateTlfItemIndex;

export interface IHierarchyStatusContext extends HierarchyContextProps {
  dispatch: React.Dispatch<IHierarchyAction>;
  title: string;
  saveRequestStatus: IRequestStatus<HierarchySaveDto>;
  saveTocRequestStatus: IRequestStatus<SaveTocAsMajorVersionParams>;
}

export enum eShowDomainText {
  "Domain" = "Domain",
  "Original text" = "Original Text",
}

export interface DomainReplacementsGroup {
  id: string;
  name: string;
  replacements: AioExternalSingle<IEditorV3>[];
  parentReplacements: AioExternalSingle<IEditorV3>[];
}
